"use client";

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
    EditableInput,
    Hue,
    Saturation,
    type HsvaColor,
    hexToHsva,
    hsvaToHex,
    validHex,
} from '@uiw/react-color';
import { cn } from '@/shared/lib/utils';

type EColorPickerProps = {
    value: string;
    onChange(color: string): void;
    onHexChange?: (hex: string) => void;
    onOpenChange?: (open: boolean) => void;
    ariaLabel?: string;
    panelLabel?: string;
    className?: string;
    triggerClassName?: string;
    panelClassName?: string;
    triggerStyle?: CSSProperties;
    triggerContent?: ReactNode;
    /** 触发器宽度，默认 60 */
    triggerWidth?: number;
    /** 触发器高度，默认 20 */
    triggerHeight?: number;
    /** 是否展示预设颜色列表 */
    showColors?: boolean;
    /** 预设颜色列表 */
    presetColors?: string[];
};

type EyeDropperResult = {
    sRGBHex: string;
};

type EyeDropperInstance = {
    open(): Promise<EyeDropperResult>;
};

type EyeDropperConstructor = new () => EyeDropperInstance;

function getEyeDropperConstructor() {
    if (typeof window === 'undefined') {
        return null;
    }

    return (window as Window & typeof globalThis & { EyeDropper?: EyeDropperConstructor }).EyeDropper ?? null;
}

const defaultHsvaColor: HsvaColor = { h: 0, s: 0, v: 100, a: 1 };

const huePointer = (props: { left?: string | number }) => (
    <div
        style={{
            boxShadow: 'rgb(0 0 0 / 60%) 0px 0px 2px',
            width: 4,
            top: 1,
            bottom: 1,
            left: props.left,
            borderRadius: 1,
            position: 'absolute',
            backgroundColor: '#fff',
        }}
    />
);

export function EColorPicker({
    value,
    onChange,
    onHexChange,
    onOpenChange,
    ariaLabel = '颜色选择器',
    panelLabel = '颜色选择面板',
    className,
    triggerClassName,
    panelClassName,
    triggerStyle,
    triggerContent,
    triggerWidth = 60,
    triggerHeight = 20,
    showColors,
    presetColors = ['transparent', '#FFFFFF', '#1F1F1F', '#9E3E37', '#91601C', '#89762B', '#257939', '#217B74', '#36669A', '#72274C'],
}: EColorPickerProps) {
    const [open, setOpen] = useState(false);
    const [hsva, setHsva] = useState<HsvaColor>(() => (validHex(value) ? hexToHsva(value) : defaultHsvaColor));
    const containerRef = useRef<HTMLDivElement | null>(null);
    const sketchPanelClassName = cn('e-color-picker-panel', panelClassName);
    const eyeDropperConstructor = getEyeDropperConstructor();

    useEffect(() => {
        if (!validHex(value)) {
            return;
        }

        const timer = window.setTimeout(() => setHsva(hexToHsva(value)), 0);
        return () => window.clearTimeout(timer);
    }, [value]);

    useEffect(() => {
        onOpenChange?.(open);
    }, [open, onOpenChange]);

    useEffect(() => {
        if (!open) {
            return;
        }

        function handlePointerDown(event: MouseEvent) {
            if (containerRef.current?.contains(event.target as Node)) {
                return;
            }

            setOpen(false);
        }

        document.addEventListener('mousedown', handlePointerDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
        };
    }, [open]);

    function handleColorChange(nextHsva: HsvaColor) {
        setHsva(nextHsva);
        onChange(hsvaToHex(nextHsva).toLowerCase());
    }

    function handleHexChange(_event: React.ChangeEvent<HTMLInputElement>, nextValue: string | number) {
        if (typeof nextValue !== 'string') {
            return;
        }

        if (onHexChange) {
            onHexChange(nextValue);
        }

        const normalizedHex = nextValue.replace(/^#/, '');
        if (!validHex(normalizedHex) || !/(3|6)/.test(String(normalizedHex.length))) {
            return;
        }

        handleColorChange(hexToHsva(normalizedHex));
    }

    async function handlePickColor() {
        if (!eyeDropperConstructor) {
            return;
        }

        try {
            const result = await new eyeDropperConstructor().open();
            if (!validHex(result.sRGBHex)) {
                return;
            }

            handleColorChange(hexToHsva(result.sRGBHex));
        } catch {
            // 用户取消吸色时保持当前颜色。
        }
    }

    return (
        <div ref={containerRef} className={cn('relative inline-flex shrink-0', className)} style={{ width: triggerWidth }}>
            <button
                type="button"
                className={cn(
                    'relative overflow-hidden rounded-[4px] cursor-pointer border-transparent p-[5px] shadow-transparent transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/35',
                    triggerClassName,
                )}
                aria-label={ariaLabel}
                aria-expanded={open}
                onClick={() => setOpen((current) => !current)}
                style={{ width: triggerWidth, height: triggerHeight, backgroundColor: value, ...triggerStyle }}
            >
                {triggerContent}
            </button>

            {open ? (
                <div
                    className={cn(
                        'absolute left-0 top-[calc(100%+8px)] z-40 overflow-hidden rounded-[16px] border-[#424242] bg-[#292929] shadow-[0px_4px_10px_0px_rgba(0,0,0,0.5)]',
                        sketchPanelClassName,
                    )}
                    role="dialog"
                    aria-label={panelLabel}
                    onClick={(e) => {
                        // 阻止面板内部的点击事件冒泡默认行为
                        // 避免在 <label> 中使用时，拖拽松开后触发 label 的默认 focus 行为导致弹窗被意外关闭
                        e.preventDefault();
                    }}
                >
                    <style>{`
            .e-color-picker-panel .w-color-editable-input input {
              background: rgba(31, 31, 31, 0.9) !important;
              border: 1px solid #404040 !important;
              border-radius: 8px !important;
              box-shadow: none !important;
              color: #D0D0D0 !important;
            }

            .e-color-picker-panel .w-color-editable-input input:focus,
            .e-color-picker-panel .w-color-editable-input input:focus-visible {
              border-color: #4096F3 !important;
              box-shadow: 0 0 0 1px #4096F3 inset !important;
              outline: none !important;
            }

            .e-color-picker-panel .w-color-editable-input span {
              color: rgba(255, 255, 255, 0.68) !important;
            }
          `}</style>
                    <div
                        className="e-color-picker-panel-root"
                        style={{
                            width: 180,
                            background: '#292929',
                            borderRadius: 16,
                            border: '1px solid #424242',
                            boxShadow: '0px 4px 10px 0px rgba(0,0,0,0.5)',
                        }}
                    >
                        <div style={{ padding: '10px 10px 8px' }}>
                            <Saturation
                                hsva={hsva}
                                style={{ width: 'auto', height: 150 }}
                                onChange={(nextColor) => handleColorChange({ ...hsva, ...nextColor, a: hsva.a })}
                            />
                            <div style={{ display: 'flex', marginTop: 4 }}>
                                <div style={{ flex: 1 }}>
                                    <Hue
                                        width="auto"
                                        height={10}
                                        hue={hsva.h}
                                        pointer={huePointer}
                                        innerProps={{
                                            style: { marginLeft: 1, marginRight: 1, borderRadius: 2 },
                                        }}
                                        onChange={(nextHue) => handleColorChange({ ...hsva, ...nextHue })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 8,
                                margin: '0 8px 4px 8px',
                            }}
                        >
                            <EditableInput
                                label="Hex"
                                value={value}
                                onChange={handleHexChange}
                                style={{ minWidth: 0, flex: '1 1 auto' }}
                            />
                            <button
                                type="button"
                                aria-label="吸取颜色"
                                disabled={!eyeDropperConstructor}
                                onClick={handlePickColor}
                                style={{
                                    width: 24,
                                    height: 24,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 6,
                                    border: '1px solid #404040',
                                    background: 'rgba(31, 31, 31, 0.9)',
                                    color: eyeDropperConstructor ? '#FAFAFA' : 'rgba(250, 250, 250, 0.3)',
                                    cursor: eyeDropperConstructor ? 'pointer' : 'not-allowed',
                                    flex: '0 0 auto',
                                    flexShrink: 0,
                                    alignSelf: 'flex-start',
                                }}
                                title={eyeDropperConstructor ? '吸取颜色' : '当前浏览器不支持吸色'}
                            >
                                <svg viewBox="0 0 512 512" width="11" height="11" aria-hidden="true" style={{ minWidth: 11, flexShrink: 0 }}>
                                    <path
                                        fill="currentColor"
                                        d="M482.8 29.23c38.9 38.98 38.9 102.17 0 141.17L381.2 271.9l9.4 9.5c12.5 12.5 12.5 32.7 0 45.2s-32.7 12.5-45.2 0l-160-160c-12.5-12.5-12.5-32.7 0-45.2s32.7-12.5 45.2 0l9.5 9.4L341.6 29.23c39-38.974 102.2-38.974 141.2 0zM55.43 323.3 176.1 202.6l45.3 45.3-120.7 120.7c-3.01 3-4.7 7-4.7 11.3V416h36.1c4.3 0 8.3-1.7 11.3-4.7l120.7-120.7 45.3 45.3-120.7 120.7c-15 15-35.4 23.4-56.6 23.4H89.69l-39.94 26.6c-12.69 8.5-29.59 6.8-40.377-4-10.786-10.8-12.459-27.7-3.998-40.4L32 422.3v-42.4c0-21.2 8.43-41.6 23.43-56.6z"
                                    />
                                </svg>
                            </button>
                        </div>

                        {showColors && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                    gap: 8,
                                    margin: '4px 8px 8px 8px',
                                }}
                            >
                                {presetColors.map((color) => {
                                    const isSelected = value.toLowerCase() === color.toLowerCase();
                                    return (
                                        <button
                                            key={color}
                                            type="button"
                                            className="nodrag h-6 w-6 rounded-full border border-white/15 transition hover:scale-110"
                                            style={{
                                                background: color === 'transparent'
                                                    ? 'repeating-conic-gradient(#444 0% 25%, transparent 0% 50%) 50% / 8px 8px'
                                                    : color,
                                                ...(isSelected && { outline: '2px solid #4096F3', outlineOffset: '1px' }),
                                            }}
                                            onClick={() => {
                                                if (color === 'transparent') {
                                                    onChange('transparent');
                                                } else {
                                                    handleColorChange(hexToHsva(color));
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
